import { useEffect, useState } from 'react';
import ValidatedInput from '../../validations/ValidatedInput';
import { sizeChartApi } from './api';
import './SizeChartsScreen.css';

const COLS = [
  { key: 'size', label: 'Size' },
  { key: 'bust', label: 'Bust' },
  { key: 'waist', label: 'Waist' },
  { key: 'hip', label: 'Hip' },
  { key: 'length', label: 'Length' },
];

const DEFAULT_ROWS = [
  { size: 'XS', bust: '32', waist: '26', hip: '34', length: '', notes: '' },
  { size: 'S', bust: '34', waist: '28', hip: '36', length: '', notes: '' },
  { size: 'M', bust: '36', waist: '30', hip: '38', length: '', notes: '' },
  { size: 'L', bust: '38', waist: '32', hip: '40', length: '', notes: '' },
  { size: 'XL', bust: '40', waist: '34', hip: '42', length: '', notes: '' },
];

export default function SizeChartsScreen({ onMessage }) {
  const flash = (t) => onMessage?.(t);
  const [charts, setCharts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [chartForm, setChartForm] = useState({
    name: 'Default Size Chart',
    isDefault: true,
    rows: DEFAULT_ROWS.map((r) => ({ ...r })),
  });

  const refresh = async () => {
    const c = await sizeChartApi.list().catch(() => ({ data: { data: [] } }));
    setCharts(c.data.data || []);
  };

  useEffect(() => {
    refresh().catch(() => flash('Could not load size charts'));
  }, []);

  const updateCell = (idx, key, value) => {
    setChartForm((f) => {
      const rows = [...f.rows];
      rows[idx] = { ...rows[idx], [key]: value };
      return { ...f, rows };
    });
  };

  const addSizeRow = () => {
    setChartForm((f) => ({
      ...f,
      rows: [
        ...f.rows,
        { size: '', bust: '', waist: '', hip: '', length: '', notes: '' },
      ],
    }));
  };

  const removeSizeRow = (idx) => {
    setChartForm((f) => ({
      ...f,
      rows: f.rows.filter((_, i) => i !== idx),
    }));
  };

  const resetForm = () => {
    setEditingId(null);
    setChartForm({
      name: 'Default Size Chart',
      isDefault: true,
      rows: DEFAULT_ROWS.map((r) => ({ ...r })),
    });
  };

  const saveChart = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const rows = chartForm.rows
        .map((r) => ({
          ...r,
          size: String(r.size || '').trim(),
        }))
        .filter((r) => r.size);
      if (!rows.length) {
        flash('Enter a size label on at least one row');
        return;
      }
      const payload = { ...chartForm, rows };
      if (editingId) {
        await sizeChartApi.update(editingId, payload);
        flash('Size chart updated');
      } else {
        await sizeChartApi.create(payload);
        flash('Size chart saved');
      }
      resetForm();
      await refresh();
    } catch (err) {
      flash(err.response?.data?.message || 'Size chart failed');
    } finally {
      setSaving(false);
    }
  };

  const loadChart = (chart) => {
    setEditingId(chart._id);
    setChartForm({
      name: chart.name || '',
      isDefault: Boolean(chart.isDefault),
      rows: (chart.rows?.length ? chart.rows : DEFAULT_ROWS).map((r) => ({
        size: r.size || '',
        bust: r.bust || '',
        waist: r.waist || '',
        hip: r.hip || '',
        length: r.length || '',
        notes: r.notes || '',
      })),
    });
  };

  const removeChart = async (chart, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete size chart "${chart.name}"? Products using it will lose their size link.`)) {
      return;
    }
    try {
      await sizeChartApi.remove(chart._id);
      flash('Size chart deleted');
      if (editingId === chart._id) resetForm();
      await refresh();
    } catch (err) {
      flash(err.response?.data?.message || 'Could not delete size chart');
    }
  };

  return (
    <div className="sc-screen">
      <form className="sc-screen__main" onSubmit={saveChart}>
        <div className="sc-screen__toolbar">
          <label className="sc-field sc-field--name">
            <span>Chart name</span>
            <ValidatedInput
              kind="text"
              name="name"
              value={chartForm.name}
              onChange={(e) => setChartForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Women — Standard"
              required
            />
          </label>

          <label className={`sc-switch${chartForm.isDefault ? ' is-on' : ''}`}>
            <input
              type="checkbox"
              checked={chartForm.isDefault}
              onChange={(e) => setChartForm((f) => ({ ...f, isDefault: e.target.checked }))}
            />
            <span className="sc-switch__track" aria-hidden />
            <span className="sc-switch__text">Use as default</span>
          </label>

          {editingId && (
            <button type="button" className="sc-screen__cancel" onClick={resetForm}>
              Cancel
            </button>
          )}

          <button type="submit" className="sc-screen__save" disabled={saving}>
            {saving ? 'Saving…' : editingId ? 'Update chart' : 'Save chart'}
          </button>
        </div>

        <p className="sc-screen__hint">Measurements in inches — attach this chart when creating products.</p>

        <div className="sc-grid-wrap">
          <div className="sc-grid" role="table" aria-label="Size measurements">
            <div className="sc-grid__head" role="row">
              {COLS.map((col) => (
                <div key={col.key} role="columnheader">
                  {col.label}
                </div>
              ))}
              <div role="columnheader" className="sc-grid__action-head" />
            </div>
            {chartForm.rows.map((row, idx) => (
              <div className="sc-grid__row" role="row" key={`row-${idx}`}>
                {COLS.map((col) => (
                  <div key={col.key} role="cell" className={col.key === 'size' ? 'is-size' : ''}>
                    <ValidatedInput
                      kind="text"
                      name={`${col.key}-${idx}`}
                      value={row[col.key]}
                      onChange={(e) => updateCell(idx, col.key, e.target.value)}
                      aria-label={`${row.size || `Row ${idx + 1}`} ${col.label}`}
                      placeholder={col.key === 'size' ? 'e.g. XXL' : ''}
                      required={col.key === 'size'}
                    />
                  </div>
                ))}
                <div role="cell" className="sc-grid__action">
                  <button
                    type="button"
                    className="sc-row-remove"
                    onClick={() => removeSizeRow(idx)}
                    disabled={chartForm.rows.length <= 1}
                    aria-label={`Remove ${row.size || 'row'}`}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button type="button" className="sc-add-row" onClick={addSizeRow}>
          + Add size
        </button>
      </form>

      <aside className="sc-screen__aside">
        <div className="sc-aside__head">
          <p className="sc-screen__label">Saved charts</p>
          <span>{charts.length}</span>
        </div>
        <div className="sc-list">
          {charts.length === 0 && (
            <p className="sc-list__empty">No charts saved yet.</p>
          )}
          {charts.map((c) => (
            <article
              key={c._id}
              className={`sc-item${editingId === c._id ? ' is-editing' : ''}`}
            >
              <button type="button" className="sc-item__main" onClick={() => loadChart(c)}>
                <div>
                  <strong>{c.name}</strong>
                  <span>{c.rows?.length || 0} sizes</span>
                </div>
                {c.isDefault && <em>Default</em>}
              </button>
              <button
                type="button"
                className="sc-item__delete"
                onClick={(e) => removeChart(c, e)}
                aria-label={`Delete ${c.name}`}
              >
                Delete
              </button>
            </article>
          ))}
        </div>
      </aside>
    </div>
  );
}
